import { InfiniteData } from '@tanstack/react-query';
import { useInfiniteQuery } from '@starsoft/common/services/query-client';
import { UserPaymentDetailsQueryKeys } from '../../../queryKeys';
import { UseUserPaymentDetailsMeQueryKeyProps } from './props';
import { AxiosError, AxiosInstance } from 'axios';
import { fetchUserPaymentDetailsMe } from './service';
import {
  GenericError,
  PaginatedResponse,
  UserPaymentDetails,
} from '@starsoft/common/models';
import { useMemo } from 'react';
import {
  useAxiosInstance,
  useInfiniteQueryCallbacks,
  useInfiniteQueryReset,
} from '@starsoft/common/hooks';
import {
  UserPaymentDetailsFetchTypeProps,
  UseUserPaymentDetailsMeProps,
} from '../props';
import { UserPaymentDetailsInputDto } from '@starsoft/common/dto';

/**
 * Custom hook to fetch infinite-scrolling user payment details for the current user
 *
 * This hook handles fetching paginated payment details with infinite scroll support,
 * filtering by payment type, and managing the query state. It uses React Query's
 * infinite query capabilities for data fetching, caching and state management.
 *
 * @template T - Type extending UserPaymentDetailsFetchTypeProps to specify valid payment types
 * @param {UseUserPaymentDetailsMeProps<T>} options - Optional configuration object
 * @param {T} options.paymentType - Optional payment type to filter results
 * @param {boolean} options.enabled - Optional flag to enable/disable the query (defaults to true)
 *
 * @returns {Object} An object containing:
 *   - data: The raw infinite query data structure
 *   - userPaymentDetails: Flattened array of all payment details across pages
 *   - isPending: Boolean indicating if initial fetch is in progress (excluding idle state)
 *   - hasNextPage: Boolean indicating if more pages are available
 *   - isFetchingNextPage: Boolean indicating if next page fetch is in progress
 *   - fetchNextPage: Function to manually fetch next page
 *   - Other infinite query state and helper methods
 */
export function useUserPaymentDetailsMeInfinity<
  T extends UserPaymentDetailsFetchTypeProps,
>(options?: UseUserPaymentDetailsMeProps<T>) {
  const { paymentType, enabled = true } = options ?? {};

  const filter: UserPaymentDetailsInputDto = {
    typeId: paymentType,
    limit: 10,
  };
  const axiosInstance: AxiosInstance = useAxiosInstance();
  const queryKey: UseUserPaymentDetailsMeQueryKeyProps = [
    UserPaymentDetailsQueryKeys.MeInfinity,
    filter,
  ];

  const { getNextPageParam, getPreviousPageParam } =
    useInfiniteQueryCallbacks();
  useInfiniteQueryReset(queryKey);

  const { data, isPending, fetchStatus, ...query } = useInfiniteQuery<
    PaginatedResponse<UserPaymentDetails>,
    AxiosError<GenericError>,
    InfiniteData<PaginatedResponse<UserPaymentDetails>>,
    UseUserPaymentDetailsMeQueryKeyProps
  >({
    queryKey,
    queryFn: queryContext =>
      fetchUserPaymentDetailsMe(queryContext, axiosInstance),
    enabled,
    getNextPageParam,
    getPreviousPageParam,
    initialPageParam: 1,
  });

  // Flatten the paginated data into a single array of payment details
  const userPaymentDetails: UserPaymentDetails[] = useMemo(
    () => data?.pages?.flatMap(page => page?.data),
    [data],
  );

  return {
    ...query,
    data,
    userPaymentDetails,
    isPending: isPending && fetchStatus !== 'idle',
  };
}
